import type { MinigameEngine } from '@wyn/content';

/**
 * Every feat each engine can report in `MinigameResult.feats`, with its name on the result screen.
 * Some are blunders (a cave-in, a snapped line): hidden deeds are made of those too.
 */
export const FEATS = {
  mining: { 'all-ore': 'Every seam worked', 'no-collapse': 'Not a pebble fell', 'cave-in': 'Buried in the dark', 'empty-handed': 'Not a lump of ore' },
  dice: { comeback: 'Back from the brink', 'house-snake-eyes': 'The house rolls snake eyes', 'caught-palming': 'Caught palming', 'rode-and-won': 'Let it ride, and won' },
  cards: { 'run-the-table': 'Ran the table', caught: 'Caught with marked cards' },
  siege: { 'breach-early': 'Breached with stones to spare' },
  jousting: { unhorsed: 'Unhorsed your foe', thrown: 'Unhorsed', untouched: 'Never touched' },
  hunting: { 'first-scout': 'Found at the first look', 'perfect-strike': 'A perfect strike' },
  alchemy: { exact: 'The recipe to the drop', erupted: 'The cauldron erupts' },
  haggling: { 'their-limit': 'Every last coin', pawned: 'Pawned' },
  woodcutting: { 'streak-five': 'Five clean cuts in a row', spent: 'Out of breath' },
  fishing: { whopper: 'A whopper', 'line-snapped': 'The line snaps' },
  smithing: { 'true-temper': 'A true temper', 'all-perfect': 'Every blow perfect' },
  brewing: { 'perfect-rack': 'Racked to the minute', 'gone-flat': 'Gone flat' },
  horsemanship: { 'steady-seat': 'A steady seat', thrown: 'Thrown' },
  lockpicking: { 'clean-pick': 'Not a pin forced', 'pick-snapped': 'A snapped pick' },
  archery: { 'perfect-card': 'A perfect card' },
  illumination: { 'flawless-hand': 'A flawless hand' },
} as const satisfies Record<MinigameEngine, Record<string, string>>;

export type FeatOf<E extends MinigameEngine> = keyof (typeof FEATS)[E] & string;

/** How a feat is kept on the player's record: `<engine>.<feat>`, so two engines may share a word. */
export const featKey = (engine: MinigameEngine, feat: string) => `${engine}.${feat}`;

/** A feat's name on screen; the bare key if it isn't listed. */
export const featName = (engine: MinigameEngine, feat: string): string => (FEATS[engine] as Record<string, string>)[feat] ?? feat;
